import type { Dispatch, MutableRefObject, SetStateAction } from 'react'
import type { TranscriptItem } from 'types'
import { hasDurableTranscriptSideEffect } from './transcript-side-effects.js'

const UI_STATUS_LABEL_REGEX = /^(thinking|listening)$/i

function isLiveUiShellRow(item: TranscriptItem): boolean {
  if (item.role !== 'model' || item.isLiveTranscript !== true) return false
  if (hasDurableTranscriptSideEffect(item)) return false
  const text = item.text?.trim() ?? ''
  if (!text && !item.attachment) return true
  if (UI_STATUS_LABEL_REGEX.test(text)) return true
  return false
}

/** Live-owned model row that should be dropped when the Live session ends. */
function isLiveOrphanModelRow(item: TranscriptItem): boolean {
  if (item.role !== 'model' || item.isLiveTranscript !== true) return false
  if (isLiveUiShellRow(item)) return true
  if (hasDurableTranscriptSideEffect(item)) return false
  const text = item.text?.trim() ?? ''
  if (!text) return true
  if (!item.isFinal || item.status === 'streaming') return true
  return false
}

function promoteLiveModelRow(item: TranscriptItem): TranscriptItem {
  if (item.role !== 'model' || item.isLiveTranscript !== true) return item
  if (item.isFinal && item.status === 'complete') return item
  return {
    ...item,
    isFinal: true,
    status: 'complete',
    ...(hasDurableTranscriptSideEffect(item) ? { isLiveTranscript: false } : {}),
  }
}

export interface ClearComposerGenerationStateArgs {
  loadingId: string | null
  setTranscript: Dispatch<SetStateAction<TranscriptItem[]>>
  setIsThinking: (value: boolean) => void
  currentLoadingIdRef: MutableRefObject<string | null>
  restTurnOwnerRef?: MutableRefObject<{ loadingId: string; startedAt: number } | null>
  mode?: 'finalize' | 'remove' | 'error'
  errorMessage?: string
}

/** Clear REST thinking state and finalize/remove any in-flight streaming transcript rows. */
export function clearComposerGenerationState({
  loadingId,
  setTranscript,
  setIsThinking,
  currentLoadingIdRef,
  restTurnOwnerRef,
  mode = 'remove',
  errorMessage,
}: ClearComposerGenerationStateArgs): void {
  const activeLoadingId = loadingId ?? currentLoadingIdRef.current

  currentLoadingIdRef.current = null
  if (restTurnOwnerRef?.current && (!activeLoadingId || restTurnOwnerRef.current.loadingId === activeLoadingId)) {
    restTurnOwnerRef.current = null
  }
  setIsThinking(false)

  setTranscript(prev => {
    const hasStreaming = prev.some(item => item.status === 'streaming')
    const hasTarget = activeLoadingId ? prev.some(item => item.id === activeLoadingId) : false
    if (!hasStreaming && !hasTarget) return prev

    if (mode === 'remove') {
      return prev
        .filter(item => (activeLoadingId ? item.id !== activeLoadingId : true))
        .map(item =>
          item.status === 'streaming'
            ? { ...item, isFinal: true, status: 'complete' as const }
            : item
        )
    }

    if (mode === 'error') {
      return prev.map(item => {
        if (activeLoadingId && item.id === activeLoadingId) {
          return {
            ...item,
            isFinal: true,
            status: 'error' as const,
            error: { type: 'unknown' as const, message: errorMessage ?? 'Interrupted' },
          }
        }
        if (item.status === 'streaming') {
          return { ...item, isFinal: true, status: 'complete' as const }
        }
        return item
      })
    }

    return prev.map(item =>
      (activeLoadingId && item.id === activeLoadingId) || item.status === 'streaming'
        ? { ...item, isFinal: true, status: 'complete' as const }
        : item
    )
  })
}

export interface CleanupLiveSessionGenerationStateArgs {
  setTranscript: Dispatch<SetStateAction<TranscriptItem[]>>
  setIsThinking: (value: boolean) => void
  currentLoadingIdRef?: MutableRefObject<string | null>
  restTurnOwnerRef?: MutableRefObject<{ loadingId: string; startedAt: number } | null>
  abortControllerRef?: MutableRefObject<AbortController | null>
}

function abortInFlightRestStream(
  abortControllerRef?: MutableRefObject<AbortController | null>
): void {
  const controller = abortControllerRef?.current
  if (!controller) return
  if (!controller.signal.aborted) {
    controller.abort()
  }
  abortControllerRef.current = null
}

/** Clear thinking/streaming state when Live voice or visual session ends. */
export function cleanupLiveSessionGenerationState({
  setTranscript,
  setIsThinking,
  currentLoadingIdRef,
  restTurnOwnerRef,
  abortControllerRef,
}: CleanupLiveSessionGenerationStateArgs): void {
  abortInFlightRestStream(abortControllerRef)
  const loadingId = currentLoadingIdRef?.current ?? null

  if (currentLoadingIdRef) {
    currentLoadingIdRef.current = null
  }
  if (restTurnOwnerRef) {
    restTurnOwnerRef.current = null
  }

  setTranscript(prev => {
    let changed = false
    const next = prev.flatMap(item => {
      if (loadingId && item.id === loadingId && item.role === 'model' && item.status === 'streaming') {
        changed = true
        if (hasDurableTranscriptSideEffect(item)) {
          return [{ ...item, isFinal: true, status: 'complete' as const, isLiveTranscript: false }]
        }
        return []
      }

      if (item.role !== 'model' || item.isLiveTranscript !== true) {
        // Any leftover streaming row blocks the composer ("Still finishing…").
        // Empty shells → drop; partial text → finalize so chat can continue.
        if (item.status === 'streaming') {
          changed = true
          if (!item.text?.trim() && !hasDurableTranscriptSideEffect(item)) {
            return []
          }
          return [{ ...item, isFinal: true, status: 'complete' as const }]
        }
        return [item]
      }
      if (isLiveUiShellRow(item) || isLiveOrphanModelRow(item)) {
        changed = true
        return []
      }
      if (!item.isFinal || item.status === 'streaming') {
        changed = true
        return [promoteLiveModelRow(item)]
      }
      return [item]
    })

    return changed ? next : prev
  })

  setIsThinking(false)
}
